import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { preventPickerEnterSubmit } from "@/lib/forms";
import { normalizeE164 } from "@/lib/phone";
import { CompanyPicker } from "@/components/companies/CompanyPicker";
import { useCreateContact } from "./api";
import { CONTACT_STATUSES, type ContactStatus } from "./types";
import { STAGE_DEFINITIONS, STAGE_LABELS } from "@/lib/stages";
import {
  DEFAULT_CONTACT_LEAD_TYPE,
  LEAD_TYPES,
  LEAD_TYPE_DEFINITIONS,
  LEAD_TYPE_LABELS,
  type LeadType,
} from "@/lib/lead-type";
import { SourceSelect } from "@/features/crm-fields/components/SourceSelect";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Initial lead type — the new-lead chooser passes "b2c" for the Person path. */
  defaultLeadType?: LeadType;
  /**
   * Gate the post-create navigation behind the app-wide unsaved-changes
   * guard. Defaults to a no-op that always allows navigating, so this
   * component works standalone (e.g. from ContactsList's own "New contact"
   * button, which isn't reachable from a dirty dashboard).
   */
  confirmNavigate?: () => boolean;
}

export function NewContactDialog({
  open,
  onClose,
  defaultLeadType = DEFAULT_CONTACT_LEAD_TYPE,
  confirmNavigate = () => true,
}: Props) {
  const navigate = useNavigate();
  const createContact = useCreateContact();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [jobTitle, setJobTitle] = useState("");
  const [status, setStatus] = useState<ContactStatus>("new");
  const [leadType, setLeadType] = useState<LeadType>(defaultLeadType);
  const [source, setSource] = useState<string>("manual");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Clear local error state and re-apply the chooser's lead type on (re)open.
  useEffect(() => {
    if (!open) {
      setError(null);
    } else {
      setLeadType(defaultLeadType);
    }
  }, [open, defaultLeadType]);

  function resetForm() {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setCompany("");
    setCompanyId(null);
    setJobTitle("");
    setStatus("new");
    setLeadType(defaultLeadType);
    setSource("manual");
    setNotes("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    // Require at least one identifying field — first name, last name, email,
    // or phone. A blank contact is rarely useful and creates merge confusion
    // later. Mirrors the deal-form's "title required" guard.
    const hasIdentifier =
      firstName.trim() || lastName.trim() || email.trim() || phone.trim();
    if (!hasIdentifier) {
      setError("Provide a name, email, or phone — at least one is required.");
      return;
    }

    if (!confirmNavigate()) return;

    // Normalize phone to E.164 if provided. Empty stays empty.
    let normalizedPhone: string | null = null;
    if (phone.trim()) {
      const e164 = normalizeE164(phone);
      // The phone helper returns "+" for empty input — guard against it
      // so we don't ship junk to the DB. Other invalid inputs would be
      // caught by the (eventual) DB CHECK; for now the column is nullable
      // free-form text so soft-handle it.
      normalizedPhone = e164.length > 1 ? e164 : null;
    }

    try {
      // B2C contacts are the customer themselves — don't carry company-account
      // linkage even if it was typed before switching the lead type.
      const isB2c = leadType === "b2c";
      const contact = await createContact.mutateAsync({
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim().toLowerCase() || null,
        phone: normalizedPhone,
        company: isB2c ? null : company.trim() || null,
        company_id: isB2c ? null : companyId,
        job_title: jobTitle.trim() || null,
        status,
        lead_type: leadType,
        source,
        notes: notes.trim() || null,
      });
      onClose();
      resetForm();
      navigate(`/contacts/${contact.id}`);
    } catch (e) {
      // The most common error here is the unique-per-org email constraint.
      // Surface that with a friendlier message than Postgres's default.
      const message = e instanceof Error ? e.message : "Create failed";
      if (message.toLowerCase().includes("contacts_email_unique_per_org")) {
        setError(
          `A contact with email ${email.trim().toLowerCase()} already exists in this organization.`,
        );
      } else {
        setError(message);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New contact</DialogTitle>
          <DialogDescription>
            Add a contact. You can refine address, social profiles, and more
            after creation.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit} onKeyDown={preventPickerEnterSubmit}>
          <div>
            <Label className="mb-1.5 block">Lead type</Label>
            <Select
              value={leadType}
              onValueChange={(v) => setLeadType(v as LeadType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    <span title={LEAD_TYPE_DEFINITIONS[t]}>
                      {LEAD_TYPE_LABELS[t]}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="contact-first-name" className="mb-1.5 block">
                First name
              </Label>
              <Input
                id="contact-first-name"
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Sam"
              />
            </div>
            <div>
              <Label htmlFor="contact-last-name" className="mb-1.5 block">
                Last name
              </Label>
              <Input
                id="contact-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Patel"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="contact-email" className="mb-1.5 block">
              Email
            </Label>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sam@example.com"
              autoComplete="off"
            />
          </div>

          <div>
            <Label htmlFor="contact-phone" className="mb-1.5 block">
              Phone
            </Label>
            <Input
              id="contact-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              autoComplete="off"
            />
          </div>

          {/* Company-account fields only apply to B2B leads — for B2C the
              person is the customer, so they're hidden (see lead_type). */}
          <div className="grid grid-cols-2 gap-3">
            {leadType === "b2b" && (
              <div>
                <Label htmlFor="contact-company" className="mb-1.5 block">
                  Company
                </Label>
                <Input
                  id="contact-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Co"
                />
              </div>
            )}
            <div>
              <Label htmlFor="contact-job-title" className="mb-1.5 block">
                Job title
              </Label>
              <Input
                id="contact-job-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="VP Sales"
              />
            </div>
          </div>

          {leadType === "b2b" && (
            <CompanyPicker
              value={companyId}
              onChange={setCompanyId}
              label="Company (account)"
            />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Stage</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as ContactStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      <span title={STAGE_DEFINITIONS[s]}>{STAGE_LABELS[s]}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Source</Label>
              <SourceSelect value={source} onChange={setSource} />
            </div>
          </div>

          <div>
            <Label htmlFor="contact-notes" className="mb-1.5 block">
              Notes (optional)
            </Label>
            <Textarea
              id="contact-notes"
              className="min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Met at the conference, follow up next week…"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createContact.isPending}>
              {createContact.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" /> Create contact
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
